/**
 * @file index.ts
 * @module types/schemas/price-catalog/index
 * @description TypeScript schema types for the price catalog module.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.3.5
 */

import { z } from "zod";
// Zod Schema for Grid Edits (Nested: Product -> Variant -> Quantities)
export const quantityChangeSchema = z.object({
  newCp: z.coerce.number().min(0, "Invalid CP").optional().default(0),
  newSp: z.coerce.number().min(0, "Invalid SP").optional().default(0),
  oldCp: z.number().optional(),
  oldSp: z.number().optional(),
  type: z.number().optional(),
  unitLabel: z.string().optional(),
});

export const variantChangeSchema = z.object({
  productId: z.string().optional(),
  variantId: z.string().optional(),
  productTitle: z.string().optional(),
  variantTitle: z.string().optional(),
  draftPO: z.boolean().optional(),
  oldDraftPO: z.boolean().optional(),
  doih: z.coerce.number().min(0, "Invalid DOIH").optional().default(0),
  oldDoih: z.number().optional(),
  originalVariantItem: z.any().optional(),
  quantities: z.record(z.string(), quantityChangeSchema).optional().default({}),
});

export const productChangeSchema = z.object({
  productId: z.string().optional(),
  productTitle: z.string().optional(),
  isTaxable: z.boolean().optional(),
  oldIsTaxable: z.boolean().optional(),
  taxPercentage: z.coerce.number().optional().default(18),
  oldTaxPercentage: z.number().optional(),
  originalProductItem: z.any().optional(),
});

export const batchEditSchema = z.object({
  products: z.record(z.string(), productChangeSchema).optional().default({}),
  variants: z.record(z.string(), variantChangeSchema).optional().default({}),
});

export type BatchEditFormValues = z.infer<typeof batchEditSchema>;
